import { Head, Link } from '@inertiajs/react';
import { SignupForm } from './components/SignupForm';
import { motion } from 'framer-motion';
import {
  UserPlus,
  ClipboardList,
  Users,
  TrendingUp,
  CheckCircle2,
  Info,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export default function Register() {
  return (
    <>
      <Head title="Sign up - Project SHORT" />
      <div className="grid h-full lg:grid-cols-2 mb-20">
        <div className="flex flex-col items-center justify-center gap-4 px-6 md:px-10">
          <div className="w-full max-w-lg flex flex-col gap-4">
            <Alert variant="default" className="border-blue-200 bg-blue-50">
              <Info className="text-blue-600" />
              <AlertTitle className="text-gray-900">
                Please apply on the track that matches your degree
              </AlertTitle>
              <AlertDescription className="gap-0 text-gray-700">
                <p>
                  <span className="font-semibold text-gray-900">
                    Pre-health:
                  </span>{' '}
                  MD/DO, MD, MD/MPH, MD/PhD, MD/MBA, MD/JD
                </p>
                <p>
                  <span className="font-semibold text-gray-900">
                    Pre-graduate:
                  </span>{' '}
                  PsyD, PhD
                </p>
              </AlertDescription>
            </Alert>
            <SignupForm />
          </div>
          <div className="text-sm text-center">
            Already have an account?{' '}
            <Link href="/session/new" className="underline underline-offset-4">
              Login
            </Link>
          </div>
        </div>
        <div className="relative hidden p-12 lg:flex lg:flex-col lg:justify-center bg-gradient-to-br from-primary/5 via-purple-50 to-blue-50 dark:from-primary/10 dark:via-purple-900/20 dark:to-blue-900/20">
          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-primary/10 blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-blue-500/10 blur-2xl"></div>
          </div>

          <div className="relative z-10 max-w-lg mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-10 text-center"
            >
              {/* <Badge className="px-4 py-2 mb-6 border shadow-sm text-primary bg-white/90 dark:bg-gray-900/90 border-primary/20">
                <Sparkles className="w-4 h-4 mr-2 text-primary" />
                Your Journey Starts Here
              </Badge> */}
              <h2 className="mb-4 text-3xl font-bold text-gray-900 dark:text-gray-100">
                Your journey begins here!
              </h2>
              <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-400">
                Join thousands of students and STEM professionals to create
                meaningful mentorship connections
              </p>
            </motion.div>

            {/* Steps */}
            <div className="space-y-12">
              {[
                {
                  step: 1,
                  icon: UserPlus,
                  title: 'Create your account',
                  description:
                    'Join the community by setting up your profile. Choose to be a mentor or mentee and start your journey here.',
                  current: true,
                  highlight: "You're here!",
                },
                {
                  step: 2,
                  icon: ClipboardList,
                  title: 'Complete your profile',
                  description:
                    'Tell us about yourself - your background, goals, and what you want to achieve with this community.',
                  current: false,
                  highlight: '15-20 minutes',
                },
                {
                  step: 3,
                  icon: Users,
                  title: 'Get matched',
                  description:
                    'Your profile helps us to connect you with the right mentor or mentee as per your goals.',
                  current: false,
                  highlight: 'Program Dependent',
                },
                {
                  step: 4,
                  icon: TrendingUp,
                  title: 'Mentorship begins',
                  description:
                    'Work with your mentor or mentee to build your skills and achieve your goals.',
                  current: false,
                  highlight: 'Grow together',
                },
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  className="relative"
                >
                  {/* Step connector line */}
                  {index < 3 && (
                    <div className="absolute left-7 top-20 w-0.5 h-16 bg-gradient-to-b from-gray-200 to-gray-100 dark:from-gray-700 dark:to-gray-800"></div>
                  )}

                  <div className="flex items-start gap-6">
                    {/* Step icon */}
                    <div
                      className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${item.current
                        ? 'bg-primary text-white shadow-lg shadow-primary/25'
                        : 'bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                    >
                      <item.icon className="w-6 h-6" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pt-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3
                          className={`text-lg font-semibold ${item.current
                            ? 'text-primary'
                            : 'text-gray-800 dark:text-gray-200'
                            }`}
                        >
                          {item.title}
                        </h3>
                        {item.current && (
                          <CheckCircle2 className="flex-shrink-0 w-5 h-5 text-green-500" />
                        )}
                        <Badge
                          variant="secondary"
                          className="px-2 py-1 text-xs border-primary/20 bg-primary/10 text-primary"
                        >
                          {item.highlight || 'Step ' + item.step}
                        </Badge>
                      </div>
                      <p
                        className={`text-sm leading-relaxed ${item.current
                          ? 'text-gray-700 dark:text-gray-300'
                          : 'text-gray-600 dark:text-gray-400'
                          }`}
                      >
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Success metrics */}
            {/* <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="p-6 mt-8 border bg-white/60 dark:bg-gray-900/60 rounded-xl border-gray-200/50 dark:border-gray-700/50"
            >
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-primary">2.5K+</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Active Mentors
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">95%</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Success Rate
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">5K+</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Connections Made
                  </div>
                </div>
              </div>
            </motion.div> */}
          </div>
        </div>
      </div>
    </>
  );
}
